import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { videos, type Video } from '@/mocks/videos';
import { articles, type Article } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<Video | Article | null>(null);
  const [itemType, setItemType] = useState<'video' | 'article'>('video');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const video = videos.find((v) => v.id === id);
    if (video) {
      setItem(video);
      setItemType('video');
      return;
    }

    const article = articles.find((a) => a.id === id);
    if (article) {
      setItem(article);
      setItemType('article');
    }
  }, [id]);

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4 block"></i>
          <h2 className="text-xl font-bold text-foreground-900 mb-2 font-heading">内容未找到</h2>
          <p className="text-sm text-foreground-600 mb-4">您访问的内容不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const isVideo = itemType === 'video';
  const videoItem = isVideo ? (item as Video) : null;
  const articleItem = !isVideo ? (item as Article) : null;

  const relatedVideos = isVideo
    ? videos.filter((v) => v.id !== item.id && v.category === videoItem!.category).slice(0, 4)
    : [];

  const relatedArticles = !isVideo
    ? articles.filter((a) => a.id !== item.id && a.category === articleItem!.category).slice(0, 4)
    : [];

  const schemaData = isVideo
    ? {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: videoItem!.title,
        description: videoItem!.description,
        image: videoItem!.thumbnail,
        datePublished: `${videoItem!.year}`,
        director: { '@type': 'Person', name: videoItem!.director },
        actor: videoItem!.actors.map((a) => ({ '@type': 'Person', name: a })),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: videoItem!.rating,
          bestRating: '10',
          ratingCount: videoItem!.views,
        },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleItem!.title,
        description: articleItem!.excerpt,
        image: articleItem!.image,
        datePublished: articleItem!.date,
        author: { '@type': 'Person', name: articleItem!.author },
      };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91free.com' },
              { '@type': 'ListItem', position: 2, name: isVideo ? '视频' : '资讯', item: `https://91free.com/category?tab=${isVideo ? '视频' : '资讯'}` },
              { '@type': 'ListItem', position: 3, name: item.title },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: isVideo ? '视频' : '资讯', href: `/category?tab=${isVideo ? '视频' : '资讯'}` },
                  { label: item.title },
                ]}
              />
            </div>

            <article className="mb-10">
              {isVideo && videoItem && (
                <>
                  <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-6 bg-background-950">
                    <img
                      src={videoItem.thumbnail}
                      alt={videoItem.title}
                      className="w-full h-full object-cover"
                      title={videoItem.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white cursor-pointer hover:bg-primary-600 transition-all duration-300 hover:scale-110">
                        <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
                      </div>
                    </div>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                    {videoItem.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong>{videoItem.rating}</strong> / 10
                    </span>
                    <span>|</span>
                    <span>{videoItem.duration}</span>
                    <span>|</span>
                    <span>{videoItem.quality}</span>
                    <span>|</span>
                    <span>{videoItem.year}年</span>
                    <span>|</span>
                    <span>{(videoItem.views / 10000).toFixed(1)}万次观看</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {videoItem.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4 mb-6 text-sm text-foreground-600">
                    <span>
                      <strong className="text-foreground-900">导演：</strong>{videoItem.director}
                    </span>
                    <span>
                      <strong className="text-foreground-900">主演：</strong>{videoItem.actors.join('、')}
                    </span>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-8">
                    <h2 className="text-lg font-bold text-foreground-900 mb-3 font-heading">影片简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">{videoItem.description}</p>
                    <div className="mt-4 text-sm text-foreground-700 leading-relaxed space-y-3">
                      {videoItem.content.split('\n\n').map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!isVideo && articleItem && (
                <>
                  <div className="relative aspect-[21/9] rounded-xl overflow-hidden mb-6">
                    <img
                      src={articleItem.image}
                      alt={articleItem.title}
                      className="w-full h-full object-cover"
                      title={articleItem.title}
                    />
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                    {articleItem.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {articleItem.author}
                    </span>
                    <span>|</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {articleItem.date}
                    </span>
                    <span>|</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {articleItem.views} 次阅读
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {articleItem.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="prose-custom">
                    <div
                      className="text-sm text-foreground-700 leading-relaxed space-y-4"
                      dangerouslySetInnerHTML={{ __html: articleItem.content }}
                    />
                  </div>
                </>
              )}
            </article>

            {relatedVideos.length > 0 && (
              <section className="mb-14">
                <h3 className="text-xl font-bold text-foreground-900 mb-6 font-heading">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              </section>
            )}

            {relatedArticles.length > 0 && (
              <section className="mb-14">
                <h3 className="text-xl font-bold text-foreground-900 mb-6 font-heading">相关资讯</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="horizontal" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}